import React, { useState, useEffect } from 'react';
import { Page, ImageElement } from '../types/storybook';
import { aiAPI, getImageUrl } from '../utils/api';
import './ResolutionCheckerModal.css';

// Canvas sizes mapping (100px = 1 inch)
const CANVAS_SIZES = [
  { value: '5x8', width: 500, height: 800 },
  { value: '5.06x7.81', width: 506, height: 781 },
  { value: '5.25x8', width: 525, height: 800 },
  { value: '5.5x8.5', width: 550, height: 850 },
  { value: '6x9', width: 600, height: 900 },
  { value: '6.14x9.21', width: 614, height: 921 },
  { value: '6.69x9.61', width: 669, height: 961 },
  { value: '7x10', width: 700, height: 1000 },
  { value: '7.44x9.69', width: 744, height: 969 },
  { value: '7.5x9.25', width: 750, height: 925 },
  { value: '8x10', width: 800, height: 1000 },
  { value: '8.25x6', width: 825, height: 600 },
  { value: '8.25x8.25', width: 825, height: 825 },
  { value: '8.5x8.5', width: 850, height: 850 },
  { value: '8.5x11', width: 850, height: 1100 },
  { value: '8.27x11.69', width: 827, height: 1169 },
  { value: '5.5x8.5-hc', width: 550, height: 850 },
  { value: '6x9-hc', width: 600, height: 900 },
  { value: '6.14x9.21-hc', width: 614, height: 921 },
  { value: '7x10-hc', width: 700, height: 1000 },
  { value: '8.25x11-hc', width: 825, height: 1100 },
];

const PRINT_DPI = 300;
const UPSCALE_TOKEN_COST = 10; // 10 tokens per upscale
const MAX_OUTPUT_PIXELS = 17_000_000; // Imagen 4 max output: 17MP

interface ResolutionIssue {
  pageIndex: number;
  pageNumber: number;
  element: ImageElement;
  naturalWidth: number;
  naturalHeight: number;
  requiredWidth: number;
  requiredHeight: number;
  recommendedFactor: 'x2' | 'x3' | 'x4' | null;
  widthRatio: number;
  heightRatio: number;
  status: 'idle' | 'upscaling' | 'done' | 'error';
  errorMessage?: string;
}

interface ResolutionCheckerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pages: Page[];
  currentPageIndex: number;
  canvasSize: string;
  onUpscaleImage: (imageUrl: string, upscaleFactor: 'x2' | 'x3' | 'x4') => Promise<string>;
  onUpdateImageElement: (pageIndex: number, imageId: string, updates: Partial<ImageElement>) => void;
}

const ResolutionCheckerModal: React.FC<ResolutionCheckerModalProps> = ({
  isOpen,
  onClose,
  pages,
  currentPageIndex,
  canvasSize,
  onUpscaleImage,
  onUpdateImageElement,
}) => {
  const [mode, setMode] = useState<'current' | 'all'>('current');
  const [issues, setIssues] = useState<ResolutionIssue[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const [isUpscalingAll, setIsUpscalingAll] = useState(false);
  const [pendingRecheck, setPendingRecheck] = useState(false);

  const sizeConfig = CANVAS_SIZES.find(s => s.value === canvasSize) || CANVAS_SIZES[3];

  useEffect(() => {
    if (isOpen) {
      setIssues([]);
      setCheckComplete(false);
      setMode('current');
      setPendingRecheck(false);
    }
  }, [isOpen]);

  // Re-check after upscale-all once pages have updated
  useEffect(() => {
    if (pendingRecheck && !isUpscalingAll) {
      setPendingRecheck(false);
      // Use setTimeout to let React batch the render first
      const timer = setTimeout(() => checkResolution(true), 300);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecheck, pages, isUpscalingAll]);

  if (!isOpen) return null;

  const calculateRequiredResolution = (element: ImageElement) => {
    // Element width/height on canvas are in canvas units (100px = 1 inch)
    const displayWidthInches = element.width / 100;
    const displayHeightInches = element.height / 100;
    const requiredWidth = Math.ceil(displayWidthInches * PRINT_DPI);
    const requiredHeight = Math.ceil(displayHeightInches * PRINT_DPI);
    return { requiredWidth, requiredHeight };
  };

  const determineUpscaleFactor = (
    naturalWidth: number,
    naturalHeight: number,
    requiredWidth: number,
    requiredHeight: number
  ): 'x2' | 'x3' | 'x4' | null => {
    const widthRatio = requiredWidth / naturalWidth;
    const heightRatio = requiredHeight / naturalHeight;
    const maxRatio = Math.max(widthRatio, heightRatio);

    // Check which factors would stay within 17MP output limit
    const inputPixels = naturalWidth * naturalHeight;
    const canX2 = (inputPixels * 4) <= MAX_OUTPUT_PIXELS;
    const canX3 = (inputPixels * 9) <= MAX_OUTPUT_PIXELS;
    const canX4 = (inputPixels * 16) <= MAX_OUTPUT_PIXELS;

    if (maxRatio <= 2 && canX2) return 'x2';
    if (maxRatio <= 3 && canX3) return 'x3';
    if (canX4) return 'x4';
    if (canX3) return 'x3';
    if (canX2) return 'x2';
    return null; // Already at max — cannot upscale further
  };

  const checkResolution = async (forceRefresh = false) => {
    setIsChecking(true);
    setCheckComplete(false);
    setIssues([]);

    const pagesToCheck = mode === 'current' 
      ? [{ page: pages[currentPageIndex], index: currentPageIndex }]
      : pages.map((page, index) => ({ page, index }));

    const foundIssues: ResolutionIssue[] = [];

    for (const { page, index } of pagesToCheck) {
      for (const element of page.imageElements) {
        try {
          const resolvedUrl = getImageUrl(element.url);
          
          // Check if we already have natural dimensions cached
          let naturalWidth = (!forceRefresh && element.naturalWidth) || 0;
          let naturalHeight = (!forceRefresh && element.naturalHeight) || 0;
          
          if (!naturalWidth || !naturalHeight) {
            const result = await aiAPI.checkImageResolution(resolvedUrl);
            if (result.success) {
              naturalWidth = result.width;
              naturalHeight = result.height;
              // Cache the natural dimensions on the element
              onUpdateImageElement(index, element.id, {
                naturalWidth: result.width,
                naturalHeight: result.height,
              });
            } else {
              continue;
            }
          }

          const { requiredWidth, requiredHeight } = calculateRequiredResolution(element);
          const widthRatio = requiredWidth / naturalWidth;
          const heightRatio = requiredHeight / naturalHeight;

          // If either dimension needs more than 1.1x the current resolution, it's an issue
          if (widthRatio > 1.1 || heightRatio > 1.1) {
            const recommendedFactor = determineUpscaleFactor(
              naturalWidth,
              naturalHeight,
              requiredWidth,
              requiredHeight
            );

            foundIssues.push({
              pageIndex: index,
              pageNumber: page.pageNumber,
              element,
              naturalWidth,
              naturalHeight,
              requiredWidth,
              requiredHeight,
              recommendedFactor,
              widthRatio,
              heightRatio,
              status: recommendedFactor ? 'idle' : 'error',
              errorMessage: recommendedFactor ? undefined : `Already ${naturalWidth}×${naturalHeight}px — too large to upscale further (17MP limit)`,
            });
          }
        } catch (error) {
          console.error('Error checking image resolution:', error);
        }
      }
    }

    setIssues(foundIssues);
    setIsChecking(false);
    setCheckComplete(true);
  };

  const handleUpscaleSingle = async (issueIndex: number) => {
    const issue = issues[issueIndex];
    if (!issue.recommendedFactor) return; // Can't upscale — already at max

    setIssues(prev => prev.map((item, i) =>
      i === issueIndex ? { ...item, status: 'upscaling' } : item
    ));

    try {
      const newUrl = await onUpscaleImage(issue.element.url, issue.recommendedFactor);
      
      // Update the element with new URL and clear natural dimensions so they get re-checked
      onUpdateImageElement(issue.pageIndex, issue.element.id, {
        url: newUrl,
        naturalWidth: undefined,
        naturalHeight: undefined,
        upscaleCount: (issue.element.upscaleCount || 0) + 1,
      });

      setIssues(prev => prev.map((item, i) =>
        i === issueIndex ? { ...item, status: 'done' } : item
      ));
    } catch (error: any) {
      setIssues(prev => prev.map((item, i) =>
        i === issueIndex
          ? { ...item, status: 'error', errorMessage: error.message || 'Upscale failed' }
          : item
      ));
    }
  };

  const handleUpscaleAll = async () => {
    const pendingIssues = issues.filter(i => i.status === 'idle' && i.recommendedFactor);
    if (pendingIssues.length === 0) return;

    const totalTokens = pendingIssues.length * UPSCALE_TOKEN_COST;
    if (!window.confirm(
      `Upscale ${pendingIssues.length} image${pendingIssues.length > 1 ? 's' : ''}?\n\nToken cost: ${totalTokens} tokens (${pendingIssues.length} × ${UPSCALE_TOKEN_COST} tokens)\n\nThis will use AI tokens from your account.`
    )) {
      return;
    }

    setIsUpscalingAll(true);
    for (let i = 0; i < issues.length; i++) {
      if (issues[i].status === 'idle' && issues[i].recommendedFactor) {
        await handleUpscaleSingle(i);
      }
    }
    setIsUpscalingAll(false);

    // Schedule re-check — useEffect will trigger it once pages prop updates
    setPendingRecheck(true);
  };

  const pendingCount = issues.filter(i => i.status === 'idle' && i.recommendedFactor).length;
  const doneCount = issues.filter(i => i.status === 'done').length;
  const totalEstimatedTokens = pendingCount * UPSCALE_TOKEN_COST;

  const getStatusBadge = (issue: ResolutionIssue) => {
    switch (issue.status) {
      case 'upscaling':
        return <span className="resolution-badge badge-upscaling">⏳ Upscaling...</span>;
      case 'done':
        return <span className="resolution-badge badge-done">✅ Done</span>;
      case 'error':
        return <span className="resolution-badge badge-error" title={issue.errorMessage}>❌ Error</span>;
      default:
        return <span className="resolution-badge badge-issue">⚠️ Low Resolution</span>;
    }
  };

  const getResolutionGrade = (ratio: number) => {
    if (ratio <= 1.0) return { label: 'OK', className: 'grade-ok' };
    if (ratio <= 1.5) return { label: 'Low', className: 'grade-low' };
    if (ratio <= 2.5) return { label: 'Poor', className: 'grade-poor' };
    return { label: 'Very Poor', className: 'grade-very-poor' };
  };

  return (
    <div className="resolution-modal-overlay" onClick={onClose}>
      <div className="resolution-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="resolution-modal-header">
          <h2>🔍 Print Resolution Checker</h2>
          <button className="resolution-close-button" onClick={onClose}>✕</button>
        </div>

        <div className="resolution-modal-body">
          {/* Mode Selection */}
          <div className="resolution-mode-section">
            <p className="resolution-description">
              Check if your images have enough resolution for high-quality printing at 300 DPI. 
              Images that are too small will appear blurry when printed.
            </p>
            <div className="resolution-mode-buttons">
              <button
                className={`resolution-mode-btn ${mode === 'current' ? 'active' : ''}`}
                onClick={() => { setMode('current'); setIssues([]); setCheckComplete(false); }}
              >
                📄 Current Page (Page {pages[currentPageIndex]?.pageNumber || currentPageIndex + 1})
              </button>
              <button
                className={`resolution-mode-btn ${mode === 'all' ? 'active' : ''}`}
                onClick={() => { setMode('all'); setIssues([]); setCheckComplete(false); }}
              >
                📚 All Pages ({pages.length} pages)
              </button>
            </div>
            <button
              className="btn btn-primary resolution-check-btn"
              onClick={() => checkResolution()}
              disabled={isChecking}
            >
              {isChecking ? '⏳ Checking...' : '🔍 Check Resolution'}
            </button>
          </div>

          {/* Results */}
          {checkComplete && (
            <div className="resolution-results">
              {issues.length === 0 ? (
                <div className="resolution-all-good">
                  <span className="resolution-all-good-icon">✅</span>
                  <h3>All images look great!</h3>
                  <p>All images on {mode === 'current' ? 'this page' : 'all pages'} have sufficient resolution for 300 DPI printing.</p>
                </div>
              ) : (
                <>
                  <div className="resolution-summary">
                    <div className="resolution-summary-stats">
                      <span className="stat-issues">⚠️ {pendingCount} issue{pendingCount !== 1 ? 's' : ''} found</span>
                      {doneCount > 0 && <span className="stat-done">✅ {doneCount} fixed</span>}
                      {pendingCount > 0 && (
                        <span className="stat-cost">
                          🪙 Est. cost: {totalEstimatedTokens} tokens
                        </span>
                      )}
                    </div>
                    {pendingCount > 0 && (
                      <button
                        className="btn btn-primary resolution-upscale-all-btn"
                        onClick={handleUpscaleAll}
                        disabled={isUpscalingAll}
                      >
                        {isUpscalingAll
                          ? `⏳ Upscaling (${doneCount}/${issues.length})...`
                          : `🚀 Upscale All (${pendingCount} images · ${totalEstimatedTokens} tokens)`}
                      </button>
                    )}
                  </div>

                  <div className="resolution-issues-list">
                    {issues.map((issue, idx) => {
                      const worstRatio = Math.max(issue.widthRatio, issue.heightRatio);
                      const grade = getResolutionGrade(worstRatio);
                      return (
                        <div key={`${issue.element.id}-${idx}`} className={`resolution-issue-card ${issue.status}`}>
                          <div className="resolution-issue-preview">
                            <img
                              src={getImageUrl(issue.element.url)}
                              alt={issue.element.characterName || 'Image'}
                              className="resolution-issue-thumbnail"
                            />
                          </div>
                          <div className="resolution-issue-details">
                            <div className="resolution-issue-header">
                              <span className="resolution-issue-name">
                                {issue.element.characterName || 'Image'}
                              </span>
                              <span className="resolution-issue-page">Page {issue.pageNumber}</span>
                              {getStatusBadge(issue)}
                            </div>
                            <div className="resolution-issue-info">
                              <div className="resolution-info-row">
                                <span className="info-label">Current:</span>
                                <span className="info-value">{issue.naturalWidth} × {issue.naturalHeight}px</span>
                              </div>
                              <div className="resolution-info-row">
                                <span className="info-label">Needed:</span>
                                <span className="info-value">{issue.requiredWidth} × {issue.requiredHeight}px</span>
                              </div>
                              <div className="resolution-info-row">
                                <span className="info-label">Display:</span>
                                <span className="info-value">
                                  {(issue.element.width / 100).toFixed(1)}" × {(issue.element.height / 100).toFixed(1)}"
                                </span>
                              </div>
                              <div className="resolution-info-row">
                                <span className="info-label">Quality:</span>
                                <span className={`info-value ${grade.className}`}>{grade.label}</span>
                              </div>
                            </div>
                            <div className="resolution-issue-actions">
                              {issue.recommendedFactor ? (
                                <>
                                  <span className="resolution-factor-badge">
                                    Recommended: {issue.recommendedFactor} upscale
                                  </span>
                                  {issue.status === 'idle' && (
                                    <button
                                      className="btn btn-primary btn-small resolution-upscale-btn"
                                      onClick={() => {
                                        if (window.confirm(
                                          `Upscale this image (${issue.recommendedFactor})?\n\nCost: ${UPSCALE_TOKEN_COST} tokens\nThis will use AI tokens from your account.`
                                        )) {
                                          handleUpscaleSingle(idx);
                                        }
                                      }}
                                    >
                                      🔍 Upscale ({UPSCALE_TOKEN_COST} tokens)
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="resolution-factor-badge badge-max">
                                  ⚠️ At max resolution — cannot upscale further
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResolutionCheckerModal;
